import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Radio, RadioGroup,
  FormControlLabel, Paper, Divider, Dialog,
  DialogContent, IconButton, Alert
} from '@mui/material';

import axios from 'axios';
import Swal from 'sweetalert2';
import CloseIcon from '@mui/icons-material/Close';
import { Card, CardContent, CardMedia } from '@mui/material';

export default function ICDLTest({ user }) {
  const [isLoading, setIsLoading] = useState(false);

  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [page, setPage] = useState(0);
  const [timeLeft, setTimeLeft] = useState(null);
  const [examStarted, setExamStarted] = useState(false);
  const [dialogImg, setDialogImg] = useState(null);

  const [fileNames, setFileNames] = useState({});
  const [fileUploadedMessage, setFileUploadedMessage] = useState({});
  const [showIntro, setShowIntro] = useState(true);

  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const [loginData, setLoginData] = useState({ username: '', password: '' });

  useEffect(() => {
    axios.get('https://filesregsiteration.sstli.com/get_icdl_questions.php')
      .then(res => setQuestions(res.data))
      .catch(err => console.error(err));
  }, []);

  // تقسيم الاسئلة (اختياري + عملي)
  const mcqQuestions = questions.filter(q => !q.isPractical);
  const practicalQuestions = questions.filter(q => q.isPractical);

  // ترتيب: اختياري ثم عملي (زي ما كنت عامل)
  const combinedQuestions = [...mcqQuestions, ...practicalQuestions];

  useEffect(() => {
    if (!examStarted) return;

    const startTime = Date.now();
    const timer = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      const remaining = 120 * 60 - elapsed; // 120 دقيقة

      if (remaining <= 0) {
        clearInterval(timer);
        handleSubmit();
        return;
      }
      setTimeLeft(remaining);
    }, 1000);

    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examStarted]);

  const handleSelect = (questionId, option) => {
    setAnswers(prev => ({ ...prev, [questionId]: option }));
  };

  const handleFileUpload = (questionId, file) => {
    if (!file) return;

    setAnswers(prev => ({ ...prev, [questionId]: file }));
    setFileNames(prev => ({ ...prev, [questionId]: file.name }));
    setFileUploadedMessage(prev => ({
      ...prev,
      [questionId]: '✅ تم رفع الملف بنجاح'
    }));
  };

  const formatTime = (t) =>
    `${Math.floor(t / 60).toString().padStart(2, '0')}:${(t % 60)
      .toString()
      .padStart(2, '0')}`;

  // ✅ تعديل التنبيه: السماح بالبحث و AI + توضيح TXT
const showInstructions = () => {
  Swal.fire({
    title: '📢 تعليمات مهمة قبل بدء الاختبار',
    html: `
      <div style="
        direction: rtl;
        text-align: right;
        line-height: 2;
        font-size: 16px;
        font-family: Cairo, Tahoma, Arial;
      ">
        <ul style="list-style: none; padding: 0; margin: 0">
          <li>✅ <b>مدة الاختبار:</b> 120 دقيقة</li>
          <li>✅ <b>عدد الأسئلة الاختيارية:</b> ${mcqQuestions.length || 14} سؤال</li>
          <li>✅ <b>عدد الأسئلة العملية:</b> ${practicalQuestions.length || 'حسب المتاح'} سؤال</li>

          <li style="margin-top:10px">⚠️ سيتم تقييمك فورًا على <b>الأسئلة الاختيارية فقط</b></li>
          <li>🛠️ الأسئلة العملية سيتم <b>مراجعتها يدويًا لاحقًا</b></li>
        </ul>

        <hr style="margin:16px 0"/>

        <p style="color:#2e7d32; font-weight:bold;">
          ✅ مسموح لك الاستعانة بأي أدوات بحث عالمية
          (مثل Google وغيرها)
          وبأي أدوات ذكاء اصطناعي
          (مثل ChatGPT)
          خلال الاختبار.
        </p>

        <p style="color:#d32f2f; font-weight:bold;">
          📌 تنويه:<br/>
          بعض ملفات الأسئلة العملية تحتوي على بيانات كثيرة،
          ولكن <u>لا يُشترط ملؤها بالكامل</u>.<br/>
          يمكنك الاكتفاء بإكمال <b>25٪ فقط</b>،
          مع التركيز على المعادلات الرياضية
          والتطبيقات العملية المطلوبة.
        </p>

        <p style="color:#1565c0; font-weight:bold;">
📝 <u>يوجد بعض الأسئلة العملية من نوع TXT</u><br/>
          في هذا النوع ستكتب الإجابة داخل
          <b>ملف نصي بامتداد .txt</b>
          ثم تقوم برفع الملف.
        </p>
      </div>
    `,
    icon: 'info',
    confirmButtonText: '🚀 ابدأ الاختبار الآن',
    confirmButtonColor: '#3085d6',
    width: 650
  }).then((result) => {
    if (result.isConfirmed) {
      setShowIntro(false);
      setExamStarted(true);
      setTimeLeft(120 * 60);
    }
  });
};


  const handleSubmit = async () => {
    setIsLoading(true);

    // Check if the user has already submitted the exam
    try {
      const checkResponse = await axios.get('https://filesregsiteration.sstli.com/ExamResults.php');
      const previousResults = checkResponse.data?.data || [];

      const isAlreadySubmitted = previousResults.some(
        (entry) => entry.UserName === loginData.username
      );

      if (isAlreadySubmitted) {
        Swal.fire({
          icon: 'warning',
          title: '⛔️ لا يمكن إعادة الاختبار',
          text: 'لقد قمت بتقديم هذا الاختبار من قبل.',
          confirmButtonText: 'حسنًا',
          confirmButtonColor: '#d33'
        });
        setIsLoading(false);
        return;
      }
    } catch (error) {
      console.error("Error checking previous submissions: ", error);
      Swal.fire({
        icon: 'error',
        title: 'خطأ',
        text: 'حدث خطأ أثناء التحقق من بياناتك. حاول مرة أخرى.',
        confirmButtonText: 'حسنًا',
        confirmButtonColor: '#d33'
      });
      setIsLoading(false);
      return;
    }

    // Calculate score for MCQ
    const correctMcqAnswers = mcqQuestions.filter(q => answers[q.Id] === q.CorrectOption);
    const score = correctMcqAnswers.length;

    const formData = new FormData();
    formData.append("username", loginData.username);
    formData.append("score", score);

    // Add uploaded files (practical)
    Object.keys(answers).forEach((questionId) => {
      const file = answers[questionId];
      if (file instanceof File) {
        formData.append("files[]", file, file.name);
      }
    });

    try {
      const response = await axios.post('https://filesregsiteration.sstli.com/submit_exam.php', formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      if (response.data.success) {
        Swal.fire({
          title: '✅ تم إنهاء الاختبار بنجاح',
          html: `
            <div style="text-align:right; font-size:16px;">
              🧮 حصلت على <b>${score} من ${mcqQuestions.length}</b> في الأسئلة الاختيارية<br/>
              🛠️ الأسئلة العملية سيتم مراجعتها يدويًا.
            </div>
          `,
          icon: 'success',
          confirmButtonText: 'حسنًا',
          confirmButtonColor: '#3085d6'
        });

        setTimeout(() => window.location.reload(), 4000);
      } else {
        Swal.fire({
          icon: 'error',
          text: response.data.message,
          confirmButtonText: 'حسنًا',
          confirmButtonColor: '#d33'
        });
      }
    } catch (error) {
      console.error("Error uploading data: ", error);
      // حتى لو حصل error في الرفع، انت عامل fallback success — سيبته زي ما هو
      Swal.fire({
        title: '✅ تم إنهاء الاختبار بنجاح',
        html: `
          <div style="text-align:right; font-size:16px;">
            🧮 حصلت على <b>${score} من ${mcqQuestions.length}</b> في الأسئلة الاختيارية<br/>
            🛠️ جاري مراجعة الأسئلة العملية.
          </div>
        `,
        icon: 'success',
        confirmButtonText: 'حسنًا',
        confirmButtonColor: '#3085d6'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ helper: accept extensions based on PracticalType (including TXT)
  const getAcceptByType = (type) => {
    const t = (type || '').toLowerCase();
    if (t === 'word') return '.doc,.docx';
    if (t === 'excel') return '.xls,.xlsx';
    if (t === 'powerpoint') return '.ppt,.pptx';
    if (t === 'txt') return '.txt';
    return '';
  };

  const renderPracticalContent = (q) => {
    const typeLower = q.PracticalType?.toLowerCase();
    const isPowerPoint = typeLower === 'powerpoint';
    const isTxt = typeLower === 'txt';

    const typeColor =
      typeLower === 'word' ? '#1e88e5' :
      typeLower === 'excel' ? '#43a047' :
      typeLower === 'powerpoint' ? '#e65100' :
      typeLower === 'txt' ? '#455a64' :
      '#607d8b';

    return (
      <Box sx={{ mt: 3 }}>
        <Box sx={{
          display: 'inline-block',
          backgroundColor: typeColor,
          color: '#fff',
          px: 2,
          py: 0.5,
          borderRadius: '12px',
          fontWeight: 'bold',
          fontSize: '14px',
          mb: 2,
          fontFamily: 'Cairo',
        }}>
          📁 نوع المهمة : {q.PracticalType}
        </Box>

        {/* المطلوب */}
        <Paper elevation={1} sx={{ p: 3, backgroundColor: '#fdfdfd', mb: 3 }}>
          <Typography style={{ fontFamily: "cairo" }} variant="h6" fontWeight="bold" color="primary" gutterBottom>
            📌 المطلوب:
          </Typography>
          <Typography sx={{ whiteSpace: 'pre-line', lineHeight: 2, fontSize: '16px', fontFamily: "cairo" }}>
            {q.QuestionText}
          </Typography>

          {/* ✅ TXT hint */}
          {isTxt && (
            <Alert severity="info" sx={{ mt: 2, fontFamily: 'Cairo' }}>
              هذا السؤال من نوع <b>TXT</b> — اكتب إجابتك داخل ملف نصي <b>.txt</b> ثم ارفع الملف.
            </Alert>
          )}
        </Paper>

        {/* الفيديو أو الصورة الرئيسية */}
        {isPowerPoint ? (
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
            <video style={{ borderRadius: 8, maxWidth: '90%' }} controls>
              <source src={`https://filesregsiteration.sstli.com/${q.FilePath}`} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </Box>
        ) : (
          q.MainImageBase64 && (
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
              <img
                src={q.MainImageBase64}
                alt="Main"
                onClick={() => setDialogImg(q.MainImageBase64)}
                style={{
                  maxWidth: '90%',
                  borderRadius: '8px',
                  border: '1px solid #ccc',
                  cursor: 'pointer'
                }}
              />
            </Box>
          )
        )}

        {/* الصور الفرعية */}
        {Array.isArray(q.SubImagesBase64) && q.SubImagesBase64.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography style={{ fontFamily: "cairo" }} variant="subtitle1" fontWeight="bold" mb={1}>
              🧷 الصور التي قد تحتاجها:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {q.SubImagesBase64.map((img, idx) => (
                <Card
                  key={idx}
                  onClick={() => setDialogImg(img)}
                  sx={{
                    width: 130,
                    cursor: 'pointer',
                    borderRadius: 1,
                    boxShadow: 1
                  }}
                >
                  <CardMedia
                    component="img"
                    image={img}
                    alt={`Sub ${idx}`}
                    sx={{ height: 100, objectFit: 'contain', p: 1 }}
                  />
                </Card>
              ))}
            </Box>
          </Box>
        )}

        {/* رفع الملف */}
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography style={{ fontFamily: "cairo" }} variant="subtitle2" fontWeight="bold" mb={1}>
            📤 قم برفع الملف الناتج:
          </Typography>

          <label htmlFor={`upload-${q.Id}`}>
            <input
              id={`upload-${q.Id}`}
              type="file"
              hidden
              accept={getAcceptByType(q.PracticalType)}
              onChange={(e) => handleFileUpload(q.Id, e.target.files[0])}
            />
            <Button style={{ fontFamily: 'cairo' }} variant="contained" component="span" color="primary">
              📎 اختر الملف
            </Button>
          </label>

          <Typography sx={{ mt: 2, fontSize: '14px', fontFamily: 'Cairo' }}>
            {fileNames[q.Id] && `الملف المرفوع: ${fileNames[q.Id]} `}
            {fileUploadedMessage[q.Id] && (
              <span style={{ color: 'green', fontSize: '12px', marginRight: 8 }}>
                {fileUploadedMessage[q.Id]}
              </span>
            )}
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderQuestion = () => {
    const q = combinedQuestions[page];
    if (!q) return null;

    const isMcq = !q.isPractical;

    const indexInGroup = isMcq
      ? mcqQuestions.findIndex(x => x.Id === q.Id)
      : practicalQuestions.findIndex(x => x.Id === q.Id);

    const totalInGroup = isMcq ? mcqQuestions.length : practicalQuestions.length;
    const groupLabel = isMcq ? '📝 الأسئلة الاختيارية' : '🛠️ الأسئلة العملية';

    return (
      <Box>
        <Box sx={{
          position: 'sticky',
          top: 0,
          zIndex: 999,
          bgcolor: '#fff',
          py: 2,
          mb: 2,
          borderBottom: '2px solid #ddd'
        }}>
          <Typography style={{ fontFamily: "cairo" }} variant="h5" fontWeight="bold">
            {groupLabel}
          </Typography>
          <Typography style={{ fontFamily: "cairo" }} variant="subtitle1" color="text.secondary">
            سؤال {indexInGroup + 1} من {totalInGroup}
          </Typography>
        </Box>

        <Paper sx={{ p: 4, mb: 3 }}>
          {!q.isPractical ? (
            <>
              <Typography style={{ fontFamily: "cairo" }} variant="h6" mb={2}>{q.QuestionText}</Typography>
              <RadioGroup
                value={answers[q.Id] || ''}
                onChange={(e) => handleSelect(q.Id, e.target.value)}
              >
                <FormControlLabel style={{ fontFamily: "cairo" }} value="A" control={<Radio />} label={q.OptionA} />
                <FormControlLabel style={{ fontFamily: "cairo" }} value="B" control={<Radio />} label={q.OptionB} />
                <FormControlLabel style={{ fontFamily: "cairo" }} value="C" control={<Radio />} label={q.OptionC} />
                <FormControlLabel style={{ fontFamily: "cairo" }} value="D" control={<Radio />} label={q.OptionD} />
              </RadioGroup>
            </>
          ) : (
            renderPracticalContent(q)
          )}
        </Paper>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3, direction: 'rtl', fontFamily: 'Cairo', width: "90%", margin: "auto" }}>

      {showIntro ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '85vh',
            background: 'linear-gradient(to bottom, #f0f4f8, #ffffff)',
            fontFamily: 'Cairo',
          }}
        >
          <Paper
            elevation={3}
            sx={{
              textAlign: 'center',
              p: 5,
              maxWidth: 550,
              width: '90%',
              borderRadius: 4,
              border: '1px solid #ddd',
              backgroundColor: '#ffffff',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            <Box>
              <Typography
                variant="h3"
                fontWeight="bold"
                gutterBottom
                sx={{ fontFamily: 'Cairo' }}
              >
                🧪 اختبار
              </Typography>

              <Typography
                variant="h6"
                sx={{ mt: 2, color: '#444', fontFamily: 'Cairo' }}
              >
                اختبار مهارات في التعامل مع الحاسب الآلي
              </Typography>

              <Typography
                variant="subtitle1"
                sx={{ mt: 1, color: '#666', fontSize: '16px', fontFamily: 'Cairo' }}
              >
                هذا الاختبار مقدم من <b>من شركة معهد السعودي المتخصص العالي للتدريب</b>
              </Typography>

              <Button
                variant="contained"
                color="primary"
                size="large"
                sx={{ mt: 4, px: 5, py: 1.5, fontSize: '18px', fontFamily: 'Cairo' }}
                onClick={() => setLoginDialogOpen(true)}
              >
                🚀 ابدأ الاختبار
              </Button>
            </Box>

            <Box sx={{ mt: 6, pt: 2, borderTop: '1px solid #eee' }}>
              <Typography variant="body2" sx={{ color: '#999', fontFamily: 'Cairo' }}>
                فريق الدعم الفني وتطوير البرمجيات
              </Typography>
            </Box>
          </Paper>
        </Box>
      ) : (
        <>
          {/* رأس الصفحة */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" fontWeight="bold" style={{ fontFamily: "cairo" }}>🧪 اختبار</Typography>
            <Box sx={{ bgcolor: '#ffe082', px: 2, py: 1, borderRadius: '8px', fontSize: '18px' }}>
              ⏱ {formatTime(timeLeft || 0)}
            </Box>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* السؤال الحالي */}
          {renderQuestion()}

          {/* أزرار التنقل */}
          <div style={{ width: "90%", display: "flex", justifyContent: "space-between", margin: "auto" }}>
            <Button
              disabled={page === 0}
              onClick={() => setPage(p => p - 1)}
              variant="outlined"
              style={{ fontFamily: "cairo" }}
            >
              ⬅️ السابق
            </Button>

            <Button
              variant="contained"
              style={{ fontFamily: "cairo" }}
              onClick={() => {
                if (page === combinedQuestions.length - 1) {
                  handleSubmit(false);
                } else {
                  setPage(p => p + 1);
                }
              }}
              disabled={isLoading}
            >
              {isLoading ? 'جاري التحميل...' : (page === combinedQuestions.length - 1 ? '🧾 إنهاء الاختبار' : 'التالي ➡️')}
            </Button>
          </div>

          {/* عرض الصورة في دايلوج */}
          <Dialog open={!!dialogImg} onClose={() => setDialogImg(null)} maxWidth="md">
            <DialogContent sx={{ textAlign: 'center' }}>
              <IconButton
                onClick={() => setDialogImg(null)}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>

              <img src={dialogImg} alt="عرض" style={{ maxWidth: '100%', borderRadius: '10px' }} />
              <Box mt={2}>
                <a href={dialogImg} download target="_blank" rel="noopener noreferrer">
                  <Button variant="contained" color="primary">تحميل الصورة</Button>
                </a>
              </Box>
            </DialogContent>
          </Dialog>
        </>
      )}

      {/* Login Dialog */}
      <Dialog
        open={loginDialogOpen}
        onClose={() => setLoginDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogContent sx={{ direction: 'rtl', fontFamily: 'Cairo' }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 3, textAlign: 'center' }}>
            تسجيل الدخول لبدء الاختبار
          </Typography>

          <Box component="form" noValidate autoComplete="off" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <input
              placeholder="اسم المستخدم"
              style={{
                width: '80%',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid #ccc',
                backgroundColor: '#f5faff',
                fontFamily: 'Cairo',
                fontSize: '16px',
                margin: 'auto'
              }}
              value={loginData.username}
              onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
            />

            <input
              type="password"
              placeholder="كلمة المرور"
              style={{
                width: '80%',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid #ccc',
                backgroundColor: '#f5faff',
                fontFamily: 'Cairo',
                fontSize: '16px',
                margin: 'auto'
              }}
              value={loginData.password}
              onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
            />

            <Button
              variant="contained"
              fullWidth
              style={{ fontFamily: 'Cairo', fontSize: '16px', padding: '10px 0', borderRadius: '8px' }}
              onClick={async () => {
                if (!loginData.username || !loginData.password) {
                  Swal.fire({
                    icon: 'warning',
                    text: 'يرجى إدخال اسم المستخدم وكلمة المرور',
                    confirmButtonText: 'حسنًا',
                    confirmButtonColor: '#d33'
                  });
                  return;
                }

                try {
                  const res = await axios.post('https://filesregsiteration.sstli.com/login_api.php', {
                    username: loginData.username,
                    password: loginData.password
                  });

                  if (res.data.success) {
                    setLoginDialogOpen(false);
                    showInstructions(); // ✅ يبدأ الاختبار
                  } else {
                    Swal.fire({
                      icon: 'error',
                      title: 'خطأ',
                      text: res.data.message
                    });
                  }
                } catch (error) {
                  Swal.fire({
                    icon: 'error',
                    title: 'فشل التحقق',
                    text: error.response?.data?.message || 'حدث خطأ أثناء الاتصال بالخادم'
                  });
                }
              }}
            >
              تسجيل الدخول
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
